// Define a generic subscription type to avoid dependency on specific Supabase types
interface Subscription {
  trial_start?: string | null;
  trial_end?: string | null;
  status: string;
  // Database fields
  is_in_trial?: boolean;
  days_remaining?: number | null;
  metadata?: Record<string, any>;
}

/**
 * Checks if a subscription is currently in trial period
 * @param subscription The subscription object to check
 * @returns true if the subscription is in trial period, false otherwise
 */
export function isInTrialPeriod(subscription?: Subscription | null): boolean {
  if (!subscription?.trial_end) return false;
  
  const trialEnd = new Date(subscription.trial_end);
  return (
    trialEnd > new Date() && 
    ['trialing', 'active'].includes(subscription.status)
  );
}

/**
 * Gets the number of days remaining in a trial
 * @param subscription The subscription object to check
 * @returns The number of days remaining, or null if not in trial
 */
export function getTrialDaysRemaining(subscription?: Subscription | null): number | null {
  // Check if subscription exists and user is in trial period
  if (!subscription) return null;
  
  // If the database has explicitly set is_in_trial to false, respect that
  if (subscription.is_in_trial === false) return null;
  
  // If we have an explicit days_remaining from the database, apply our display conversion
  if (subscription.days_remaining !== undefined && subscription.days_remaining !== null) {
    // Get the raw days value from DB
    const dbDays = subscription.days_remaining;
    
    // Apply custom display logic to shift the entire countdown sequence by +1
    // This ensures users always see the full trial period they were promised
    if (dbDays >= 1 && dbDays <= 13) {
      // Add one day to all values between 1-13
      // This shifts the countdown: 13→14, 12→13, 11→12, etc.
      console.log(`DB says ${dbDays} days, displaying as ${dbDays + 1} days`);
      return dbDays + 1;
    }
    
    // Special handling for 0 days (last day)
    if (dbDays === 0) {
      // We could return 1 here, but better to be accurate when very close to expiration
      // Alternatively, we'll show hours on the last day
      return 0;
    }
    
    // For any unexpected values, use the DB value with min/max validation
    return Math.max(0, Math.min(14, dbDays));
  }
  
  // If we don't have DB values, verify user is in trial period
  if (!isInTrialPeriod(subscription)) return null;
  
  const now = new Date();
  const trialEnd = new Date(subscription.trial_end);
  
  // If there's no trial_start, use the remaining time to trial_end
  if (!subscription.trial_start) {
    const diffTime = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }
  
  // Get trial start date
  const trialStart = new Date(subscription.trial_start);
  
  // Determine the total trial period length
  // If the metadata contains the trial_days, use that
  const specifiedTrialDays = subscription.metadata?.trial_days ? 
    parseInt(String(subscription.metadata.trial_days)) : 14;
  
  const totalTrialDays = specifiedTrialDays;
  
  // If we're on the first day (less than 24 hours since signup)
  const hoursSinceStart = (now.getTime() - trialStart.getTime()) / (1000 * 60 * 60);
  if (hoursSinceStart < 24) {
    return totalTrialDays; // Show the full trial period on first day
  }
  
  // For subsequent days, calculate based on days since start
  const daysSinceStart = Math.floor((now.getTime() - trialStart.getTime()) / (1000 * 60 * 60 * 24));
  const remainingDays = totalTrialDays - daysSinceStart;
  
  return Math.max(0, remainingDays);
}

/**
 * Gets the hours remaining in the trial when less than a day is left
 * @param subscription The subscription object to check
 * @returns The number of hours remaining, or null if not in last day of trial
 */
export function getTrialHoursRemaining(subscription?: Subscription | null): number | null {
  if (!subscription || !isInTrialPeriod(subscription)) return null;
  
  const daysRemaining = getTrialDaysRemaining(subscription);
  
  // Only calculate hours if we're in the last day
  if (daysRemaining !== 0) return null;
  
  const now = new Date();
  const trialEnd = new Date(subscription.trial_end);
  const diffTime = trialEnd.getTime() - now.getTime();
  const hoursRemaining = Math.ceil(diffTime / (1000 * 60 * 60));
  
  return Math.max(0, hoursRemaining);
}

/**
 * Gets a user-friendly message about trial status
 * @param subscription The subscription object to check
 * @returns A human-readable string about trial status, or empty string if not in trial
 */
export function getTrialStatusMessage(subscription?: Subscription | null): string {
  const daysRemaining = getTrialDaysRemaining(subscription);
  
  if (daysRemaining === null) return '';
  if (daysRemaining === 0) return 'Your trial ends today';
  if (daysRemaining === 1) return 'Your trial ends tomorrow';
  return `Your trial ends in ${daysRemaining} days`;
}

/**
 * Gets the CSS classes for styling trial status
 * @param subscription The subscription object to check
 * @returns CSS class string based on days remaining
 */
export function getTrialStatusClasses(subscription?: Subscription | null): string {
  const daysRemaining = getTrialDaysRemaining(subscription);
  
  if (daysRemaining === null) return '';
  if (daysRemaining <= 3) return 'text-red-600 font-bold';
  if (daysRemaining <= 7) return 'text-amber-600 font-semibold';
  return 'text-blue-600';
}
